import { redirect } from '@tanstack/react-router'

import type { TProfileResponse } from '@/api/auth/types'
import type { ModuleDataType } from '@/api/pengguna/types'
import type { NavItem } from '@/types/common'

export const filterSidebarByModules = (
  sidebarItems: NavItem[],
  userModules: ModuleDataType[],
  userRole?: string,
  hasPermission?: (permission: string) => boolean
): NavItem[] => {
  const userModuleCodes = userModules.map(module => module.code)

  const filterItems = (items: NavItem[]): NavItem[] => {
    return items.reduce((filtered: NavItem[], item) => {
      // Temporarily hide Manajemen Pengguna menu for all roles
      if (item.label === 'Manajeman Pengguna' || item.label === 'Pengguna') {
        return filtered
      }

      // Hide Verifikasi Pindah Domisili Petani for non-BPP users
      if (
        item.label === 'Verifikasi Pindah Domisili Petani' &&
        userRole !== 'admin_bpp'
      ) {
        return filtered
      }

      if (item.isGroupLabel) {
        if (item.groupModule) {
          const hasAnyModule = item.groupModule.some(moduleCode =>
            userModuleCodes.includes(moduleCode)
          )

          if (!hasAnyModule) {
            return filtered
          }
        }

        filtered.push(item)
        return filtered
      }

      if (item.children) {
        const filteredChildren = filterItems(item.children)

        if (filteredChildren.length > 0) {
          filtered.push({
            ...item,
            children: filteredChildren
          })
        }
        return filtered
      }

      if (item.module) {
        if (userModuleCodes.includes(item.module)) {
          filtered.push(item)
        }
        return filtered
      }

      if (item.permission) {
        if (hasPermission?.(item.permission)) {
          filtered.push(item)
        }
        return filtered
      }

      filtered.push(item)
      return filtered
    }, [])
  }

  return filterItems(sidebarItems)
}

export const requireModuleAndPermission = (
  moduleCode: string,
  permission: string
) => {
  return async ({
    context: { profile }
  }: {
    context: { profile: TProfileResponse }
  }) => {
    const module = profile?.modules?.find((m: any) => m.code === moduleCode)

    if (!module) {
      throw redirect({
        to: '/403'
      })
    }

    const hasPermission = module.permissions?.some(
      (p: any) => p.name === permission
    )

    if (!hasPermission) {
      throw redirect({
        to: '/403'
      })
    }

    return { profile, module }
  }
}

export const buildRouteModules = (
  items: Array<NavItem>
): Record<string, { module: string; permission: string } | null> => {
  const routes: Record<string, { module: string; permission: string } | null> =
    {}

  function traverse(list: Array<NavItem>) {
    for (const item of list) {
      if (item.href) {
        if (item.module && item.permission) {
          routes[item.href] = {
            module: item.module,
            permission: item.permission
          }

          const baseHref = item.href
          routes[`${baseHref}/create`] = {
            module: item.module,
            permission: `${item.module}.create`
          }
          routes[`${baseHref}/edit`] = {
            module: item.module,
            permission: `${item.module}.update`
          }
        } else {
          routes[item.href] = null
        }
      }

      if (item.children) {
        traverse(item.children)
      }
    }
  }

  traverse(items)
  return routes
}
